import React from "react";
import {Formik} from "formik";
import {FormikSubmitButton} from "../../form/FormikSubmitButton";
import {FormikTextField} from "../../form/FormikTextField";
import {useSearchTranslation} from "../../search/SearchContext";
import {useEmail} from "../../folder/useEmail";
import {InputRecipientsSearch} from "./InputRecipientsSearch";
import Box from "@mui/material/Box";
import {ShareMessageCounter} from "../ShareMessageCounter";
import {MAX_SHARE_MESSAGE_LENGTH, MAX_SHARE_RECIPIENTS} from "../shareLimits";
import {TooManyRecipientsAlert} from "../TooManyRecipientsAlert";

/**
 * Panel for sharing a document or a folder with internal DAMS users via email.
 * The server writes the mail (DE-1843); the panel sends the target, the recipients and the personal message.
 *
 * @param {number}   collectionId      - Collection ID used for recipient lookup.
 * @param {string}   modelId           - UniqueId of the document being shared.
 * @param {string}   folderId          - UniqueId of the folder being shared (if sharing a folder).
 * @param {Function} onShareSuccess    - Called after the email is sent so the
 *                                       dialog can optionally close or proceed.
 */
export const SharePrivatePanel = ({collectionId, modelId, folderId, onShareSuccess}) => {
    const t = useSearchTranslation();
    const {sendShareNotification} = useEmail();

    const handleOnSubmit = async (formValues, {resetForm}) => {
        const sent = await sendShareNotification({
            objectUuid: modelId,
            folderId: modelId ? undefined : folderId,
            recipients: formValues.recipients.map(v => v.email),
            message: formValues.message,
        });
        if (!sent) return;
        resetForm();
        if (onShareSuccess) onShareSuccess();
    };

    return (
        <Formik
            initialValues={{recipients: [], message: ""}}
            validationSchema={null}
            onSubmit={handleOnSubmit}
        >
            {({values}) => (
                <Box sx={{pt: 1, marginTop: '8px'}}>
                    <Box sx={{mb: 2}}>
                        <FormikTextField
                            formikKey="message"
                            label={t("shareDocumentLabelMessage", "Melding")}
                            required={false}
                            fullWidth
                            multiline
                            rows={6}
                            slotProps={{htmlInput: {maxLength: MAX_SHARE_MESSAGE_LENGTH}}}
                            helperText={t(
                                'dialogShareFilePubliclyAddText',
                                'Legg til en melding som skal sendes til mottagerne av delingen'
                            )}
                            autoFocus
                        />
                        <ShareMessageCounter length={values.message.length}/>
                    </Box>
                    <InputRecipientsSearch
                        formikKey="recipients"
                        label={t("shareDocumentLabelRecipients", "Mottagere")}
                        disabled={false}
                        fullWidth
                        collectionId={collectionId}
                    />
                    <TooManyRecipientsAlert count={values.recipients.length} t={t}/>
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                        <FormikSubmitButton color="primary"
                                            variant="contained"
                                            disabled={values.recipients.length === 0 || values.recipients.length > MAX_SHARE_RECIPIENTS}>
                            {t("btnShare", "Del")}
                        </FormikSubmitButton>
                    </Box>
                </Box>
            )}
        </Formik>
    );
};

